import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Eye, EyeOff } from "lucide-react";

import axiosInstance from "utils/axiosInstance";

import BreadcrumbAboveImage from "components/Common/BreadcrumbAboveImage";
import StatBox from "components/Common/StatBox";
import BorderTrailBox from 'components/Common/BorderTrailBox';
import Toast from "components/Common/Toast";   
import HoverInfo from "components/Common/HoverInfo";
import "components/Common/HoverInfo.css";

import debounce from "lodash/debounce";

import { LuRefreshCw } from "react-icons/lu";

import PointRedemptionRuleModal from "./PointRedemptionRuleModal";

import { bankList } from "./BankInfoUtils";

import townMap from "assets/images/info/townMap.png";
import bank10 from "assets/images/info/bank10.png";


const getTodayUTC = () => {
  return new Date().toISOString().split('T')[0]; // "YYYY-MM-DD"
};

const getMsUntilNextUtcMidnight = () => {
  const now = new Date();
  const nextMidnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0));
  return nextMidnight.getTime() - now.getTime();
};

const PointRedemption = ({ t }) => {

  document.title = "積分兌換 | TheChart.AI";


  // ────────────────────────────────────────────────────
  // state
  // ────────────────────────────────────────────────────
  const [myInfoPoint, setMyInfoPoint] = useState(null);
  const [currentPendingPoint, setCurrentPendingPoint] = useState(null);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [lastFetchedDate, setLastFetchedDate] = useState(null); // for tracking rate refresh
  
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [redeemPoints, setRedeemPoints] = useState("");
  const [calculatedAmount, setCalculatedAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loadingRates, setLoadingRates] = useState(true);
  // Format the option label
  const [accountOption, setAccountOption] = useState({});

  const [showPointToCurrency, setShowPointToCurrency] = useState(true);

  /* ---------- Toast ---------- */
  const [toasts, setToasts] = useState([]);   // { id, msg, type, dur }
  const pushToast = (msg, type = "success", duration = 3000) => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, type, duration }]);
  };
  const popToast = id => setToasts(t => t.filter(x => x.id !== id));

  /* ---------- points & max-redeemable ---------- */
  const pointsPerUSD = 30;
  const [maxPointsData, setMaxPointsData] = useState({});
  const [maxRedeemablePoint, setMaxRedeemablePoint] = useState(null); 





  // ────────────────────────────────────────────────────
  // data fetchers
  // ────────────────────────────────────────────────────

  const fetchUserPoints = async () => {
    try {
      const { data } = await axiosInstance.get("/knovia/myInfo/infoPoints");
      console.log('[fetchUserPoints]', data);
      setMyInfoPoint(data.totalPoints);
      setCurrentPendingPoint(data.pendingPoints);
      setMaxPointsData(data.dailyMaxPoints[getTodayUTC()]);
      setMaxRedeemablePoint(Math.ceil(data.dailyMaxPoints[getTodayUTC()].effectiveTotalPoints / 3) || 0);
    } catch (err) {
      console.error(err);
      pushToast(t("無法取得積分，請稍後再試"), "error");
    }
  };

  const fetchExchangeRates = async () => {
    setLoadingRates(true);
    try {
      const { data } = await axiosInstance.get("/knovia/myInfo/exchangeRates");
      setExchangeRates(data.rates);
      setLastFetchedDate(getTodayUTC());
    } catch (err) {
      console.error(err);
      pushToast(t("無法取得匯率，請稍後再試"), "error");
    } finally {
      setLoadingRates(false);
    }
  };

  const [bankInfo, setBankInfo] = useState({});
  const fetchBankInfo = async () => {
    try {
      const { data } = await axiosInstance.get("/knovia/myInfo/bankInfo");
      console.log('info fetched:', data);
      setBankInfo(data);
    } catch (error) {
      console.error("Failed to load bank info", error);
    }
  };


  // ────────────────────────────────────────────────────
  // effects
  // ────────────────────────────────────────────────────

  const fetchPointsData = async () => {
    await fetchExchangeRates(); 
    await fetchUserPoints();
    await fetchBankInfo();
  }

  const debouncedFetch = useCallback(
    debounce(async () => {
      try {
        await fetchExchangeRates();
        await fetchUserPoints();
        await fetchBankInfo();
        pushToast(t("已套用最新資訊"), "success");
      } catch (err) {
        console.error(err);
        pushToast(t("取得資訊失敗"), "error");
      }
    }, 1000, { leading: true, trailing: false }), // <-- Only allow once per 1s
    []
  );

  useEffect(() => { 
    fetchPointsData();
  }, []);

  const [showMasked, setShowMasked] = useState(true);
  useEffect(() => {
    if (bankInfo?.accountNumber) {
      const bankName = bankList.find(b => b.code === bankInfo.bankCode)?.name || "";

      const accountName = showMasked
        ? '*'.repeat(bankInfo.accountName.length - 1) + bankInfo.accountName.slice(-1)
        : bankInfo.accountName;

      const accountNumber = showMasked
        ? '*'.repeat(bankInfo.accountNumber.length - 5) + bankInfo.accountNumber.slice(-5)
        : bankInfo.accountNumber;

      setAccountOption({
        value: bankInfo.accountNumber,
        label: `${accountName}（${bankName} ${bankInfo.bankCode}-${accountNumber}）`,
      });
    }
  }, [bankInfo, showMasked]);

  useEffect(() => {
    if (!lastFetchedDate) return;

    const msUntilMidnight = getMsUntilNextUtcMidnight();

    const timeout = setTimeout(() => {
      console.log("UTC day changed! Refetching rates...");
      fetchExchangeRates();
    }, msUntilMidnight + 1000); // extra +1s buffer just to be safe

    return () => clearTimeout(timeout);
  }, [lastFetchedDate]);

  useEffect(() => {
    if (selectedCurrency && redeemPoints && exchangeRates) {
      const points = Math.min(redeemPoints, maxRedeemablePoint);
      const usdAmount = points / pointsPerUSD;
      const amount = usdAmount * exchangeRates[selectedCurrency.value];
      setCalculatedAmount(amount);
    } else {
      setCalculatedAmount(0);
    }
  }, [selectedCurrency, redeemPoints, exchangeRates, maxRedeemablePoint]);



  // ────────────────────────────────────────────────────
  // handlers
  // ────────────────────────────────────────────────────

  const handleRedeemPointsChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setRedeemPoints("");
      return;
    }
    const numericValue = parseInt(value, 10);
    if (numericValue <= maxRedeemablePoint) {
      setRedeemPoints(numericValue);
    } else {
      setRedeemPoints(maxRedeemablePoint);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCurrency || !redeemPoints || redeemPoints <= 0) {
      return;
    }

    setSubmitting(true);
    try {
      await axiosInstance.post("/knovia/myInfo/redeemPoints", {
        currency: selectedCurrency.value,
        points: redeemPoints,
      });

      pushToast(
        `${t("已提交申請")}: ${redeemPoints} → ` +
          `${calculatedAmount.toFixed(2)} ${selectedCurrency.label}`,
        "success",
        4000
      );

      fetchUserPoints();
      
      // reset the form
      setSelectedCurrency(null);
      setRedeemPoints("");
      setCalculatedAmount(0);
    } catch (error) {
      console.error(error);
      pushToast(t("提交失敗，請稍後再試"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const currencyOptions = exchangeRates
    ? Object.keys(exchangeRates).map((cur) => ({
        value: cur,
        label: cur,
      }))
    : [];

  const isSubmitDisabled = !selectedCurrency || !redeemPoints || redeemPoints <= 0 || submitting;

  const calculatePointValue = (currency) => {
    if (!exchangeRates) return 0;
    const usdPerPoint = 1 / pointsPerUSD;
    return (usdPerPoint * exchangeRates[currency]).toFixed(4);
  };

  const handleToggleConversion = () => {
    setShowPointToCurrency(prev => !prev);
  };


  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showColorHint, setShowColorHint] = useState(false);
  const toggleRuleModal = () => setShowRuleModal(prev => !prev);
  const customStyles = {
    control: (provided) => ({
      ...provided,
      //color: "black", // this affects placeholder
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "grey", // selected item text
    }),
    option: (provided, state) => ({
      ...provided,
      /*
      color: state.isSelected ? "white" : "#333", // options in dropdown
      backgroundColor: state.isSelected ? "#1a73e8" : "white",
      ':hover': {
        backgroundColor: "#e6f0ff",
        color: "#1a73e8",
      },
      */
    }),
  };

  return (
    <div className="page-content" 
      style={{ 
        backgroundImage: `url(${bank10})`,
        backgroundSize: "cover"
      }}
    >

      {/* toast stack */}
      {toasts.map(t => (
        <Toast
          key={t.id}
          message={t.msg}
          type={t.type}
          duration={t.duration}
          onClose={() => popToast(t.id)}
        />
      ))}

      <Container fluid>
        <BreadcrumbAboveImage title={t("我的資訊")} breadcrumbItem={t("積分兌換")} />
        
        <Card style={{ minHeight: "40rem" }}>
          <CardBody>
            <div className="d-flex justify-content-between">
              <CardTitle className="mb-4">{t("積分兌換現金")}</CardTitle>

              <Button 
                color="outline-secondary" 
                className="hover-info-button"
                onClick={debouncedFetch}
                style={{
                  padding: "0.2rem 0.75rem 0.45rem",
                  fontSize: "18px",
                  background: "#f5f5f5",
                  color: "grey",
                }}
              >
                <LuRefreshCw />
                <HoverInfo
                  label="Refresh Data"
                  styles={{ backgroundColor: "green" }}
                  activeStyles={{ backgroundColor: "yellowgreen" }}
                />
              </Button>
            </div>

            <Row className="mb-4" style={{ textAlign: "center" }}>
              <Col>

                <div style={{ display: 'flex', justifyContent: 'center', margin: '1rem 0rem 1rem 0rem' }}>
                  <BorderTrailBox
                    //trailColor="#000000"
                    trailColor="gold"
                    //borderColor="#000000"
                    borderRadius={38}
                    borderWidth={2}
                    trailSize={105}
                    speed={2.5}
                    //explode={true}
                    mainStyles={{ display: "flex", justifyContent: "center" }}
                    asideStyles={{ maxWidth: "50%" }}
                  >
                    <div style={{ 
                      padding: "1.6rem 1rem 1.3rem 1rem", 
                      backgroundColor: "white",
                      borderRadius: "38px"
                    }}>
                      <Row style={{ marginBottom: "0.3rem" }}>
                        <Col xs={12}>
                          <p style={{ margin:'0', fontSize:'20px' }}>{t("目前積分點數")}</p>
                          <div className="mt-1 mb-2">
                            {myInfoPoint !== null ? (
                              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "62px", maxHeight: "62px" }}>
                                <StatBox value={myInfoPoint || 0} loading={false} numberStyles={{ color:"gold", fontWeight: "400" }} />
                              </div>
                            ) : (
                              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "62px", maxHeight: "62px" }}>
                                <Spinner size="sm" />
                              </div>
                            )}
                          </div>
                          <h6>({t("每")} 30 {t("積分")} = 1 USD)</h6>
                        </Col>
                      </Row>
                    </div>
                  </BorderTrailBox>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center"
                  }}
                >
                  <p className="text-muted" style={{ margin: '0', fontSize: "0.8rem" }}>
                    {t("今天最多可兌換")}: {maxRedeemablePoint} points
                  </p>

                  <span
                    onClick={toggleRuleModal}
                    style={{
                      marginLeft: "0.5rem",
                      color: "#3AB5E4",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "0.75rem"
                    }}
                  >
                    <i className='bx bx-info-circle' style={{ fontSize: "1.0rem", marginRight: "0.2rem" }}></i>
                    {t("查看規則")}
                  </span>
                </div>

                <div>
                  <p className="mt-2 text-muted">
                    {t("已申請待處理的點數")}: {currentPendingPoint || 0} points
                  </p>
                </div>
              </Col>
            </Row>

            {/* Exchange Rates */}
            <Row className="mb-5">
              
                {loadingRates ? (
                  <Col xs={12} 
                    style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "10rem" }}>
                    <Spinner />
                  </Col>
                ) : (
                  <>
                    <Col xs={12} md={6}
                      className="d-flex justify-content-center justify-content-md-end pe-md-5"
                    >
                      <div>
                        <h6 className="mt-4 mb-3">{t("匯率 (以 1 USD 計)")}</h6>
                        <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                          {exchangeRates && Object.entries(exchangeRates).map(([currency, rate]) => (
                            <li key={currency} style={{ color: currency==="USD" ? "grey" : "black" }}>
                              1 USD = {rate.toFixed(2)} {currency}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Col>
                    <Col xs={12} md={6}
                      className="d-flex justify-content-center justify-content-md-start ps-md-5"
                    >
                      <div>
                        <h6 className="mt-4 mb-3" style={{ display: "flex", alignItems: "center" }}>
                          {t("每 1 積分換算")}
                          <i
                            className="bx bx-transfer-alt ms-1"
                            style={{ cursor: "pointer", color: "#71b871" }}
                            onClick={handleToggleConversion}
                            title={t("切換換算方向")}
                          ></i>
                        </h6>
                        <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                          {exchangeRates && Object.keys(exchangeRates).map((currency) => {

                            const rate = exchangeRates[currency];
                            const usdPerPoint = 1 / pointsPerUSD;

                            return (
                              <li key={currency} style={{ color: currency==="USD" ? "grey" : "black" }}>
                                {currency==="USD" ? (<i className="bx bx-pin me-1"></i>) : 
                                  (<i className="bx bx-right-arrow-alt me-1"></i>)
                                }
                                {showPointToCurrency ? (
                                  <>
                                    1 point = {(usdPerPoint * rate).toFixed(4)} {currency}
                                  </>
                                ) : (
                                  <>
                                    1 {currency} = {(1 / (usdPerPoint * rate)).toFixed(2)} points
                                  </>
                                )}
                              </li>
                            );
                          })}

                        </ul>
                      </div>
                    </Col>
                  </>
                )}
            </Row>

            {/* Redemption Form */}
            <Row style={{ display:"flex", justifyContent:"center", marginBottom: "5rem" }}>
              <Col xs={12} md={6} className="ps-5 pe-5 ps-md-0 pe-md-0 ps-xl-5 pe-xl-3">

                <Row style={{ display:"flex", justifyContent:"center" }}>

                  <Col xs={12} xl={8} style={{ marginBottom: "0.5rem" }}>
                    <div style={{ display:"flex", justifyContent: "space-between"}}>
                      <Label>{t("兌換帳戶資訊")}</Label>
                      <Button
                        size="sm"
                        color="link"
                        onClick={() => setShowMasked(prev => !prev)}
                        style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
                      >
                        {showMasked ? <EyeOff size={16} /> : <Eye size={16} />}
                        {showMasked ? "顯示" : "隱藏"}
                      </Button>
                    </div>
                    <Select
                      styles={customStyles} 
                      value={accountOption}
                      options={[accountOption]}
                      //isDisabled
                    />
                  </Col>


                  <Col xs={12} xl={8} style={{ marginBottom: "0.5rem" }}>
                    <Label>{t("輸入欲兌換的積分數量")}</Label>
                    <Input
                      type="number"
                      value={redeemPoints}
                      onChange={handleRedeemPointsChange}
                      placeholder={t(`最多 ${maxRedeemablePoint}`)}
                      min="1"
                      step="1"
                      disabled={submitting}
                    />
                  </Col>

                  <Col xs={12} xl={8} style={{ marginBottom: "0.5rem" }}>
                    <Label>{t("選擇收款貨幣")}</Label>
                    <Select
                      options={currencyOptions}
                      value={selectedCurrency}
                      onChange={setSelectedCurrency}
                      placeholder={t("請選擇")}
                      isDisabled={submitting || loadingRates}
                    />
                  </Col>

                  <Col xs={12} xl={8} style={{ marginBottom: "1.5rem" }}>
                    {calculatedAmount > 0 && (
                      <h6>{t("您將獲得")}: {calculatedAmount.toFixed(2)} {selectedCurrency?.label}</h6>
                    )}
                  </Col>

                  <Col xs={12} xl={8}
                    className="d-flex justify-content-center justify-content-md-start"
                  >
                    <Button
                      color="primary"
                      onClick={handleSubmit}
                      style={{ padding: "10px 16px" }}
                      disabled={isSubmitDisabled}
                    >
                      {submitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {t("提交中...")}
                        </>
                      ) : (
                        <>
                          <i className="bx bx-check-circle me-2"></i>
                          {t("提交兌換申請")}
                        </>
                      )}
                    </Button>
                  </Col>

                </Row>

              </Col>
            </Row>

          </CardBody>
        </Card>
      </Container>

      <PointRedemptionRuleModal 
        isOpen={showRuleModal} 
        toggle={toggleRuleModal} 
        redemptionRuleModalStyles={{ minWidth: '50%' }}
        showColorHint={showColorHint}
        setShowColorHint={setShowColorHint}
        maxPointsData={maxPointsData}
      />

    </div>
  );
};

PointRedemption.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(PointRedemption);
